// FILE: apps/admin/src/pages/Pricing.tsx
// VERSION: 1.0.0
// START_MODULE_CONTRACT
//   PURPOSE: Dedicated Admin Pricing & Tariffs page with 3 horizontal tabs (Base Markups & Fragment Rate, Spend-Volume Tiers with Live Resulting Prices Matrix, and Storefront Packs) plus pure pricing preview helpers.
//   SCOPE: buildPricingPreviewRows, buildSpendTierPreviewMatrix, buildLiveComparisonRows, exported preview types, and Pricing React component.
//   DEPENDS: ../api.js, ../components/Icons.js, ../toast.js
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   LiveComparisonPreview - Computed locked vs live RUB & Fragment GRAM comparison at current draft X multipliers
//   PremiumPreviewRow - Computed live preview row for a Telegram Premium tier (3/6/12m)
//   Pricing - Dedicated 3-tab Admin Pricing & Tariffs page component
//   PricingPreviewResult - Computed Stars and Premium preview tables for current draft X multipliers
//   SpendTierCellPrice - Resulting retail RUB, GRAM, Stars, and savings vs Base Tariff for one Spend Tier cell
//   SpendTierColumnMeta - Normalized Spend Tier column metadata for the live comparison matrix
//   SpendTierDraftInput - Operator draft input row for a Spend-Volume Commission Tier
//   SpendTierPreviewMatrix - Complete live comparison matrix across Wholesale, Base Tariff, and all Spend Tiers
//   SpendTierPreviewMatrixRow - Single product row (1..1000 Stars or Premium 3/6/12m) in the Spend Tier preview matrix
//   StarPreviewRow - Computed live preview row for a Telegram Stars package
//   buildLiveComparisonRows - Pure helper computing locked vs live retail RUB & GRAM deltas at current draft X inputs
//   buildPricingPreviewRows - Pure helper computing retail RUB/GRAM/Stars and net margin from Fragment rates and draft X inputs
//   buildSpendTierPreviewMatrix - Pure helper computing live client retail RUB/GRAM prices and savings across all Spend Tiers
// END_MODULE_MAP

import {
  type AdminPricingPreviewDto,
  type AdminSpendCommissionTierDto,
} from '@brainrotstore/api-contracts';
import {
  comparePricingSnapshots,
  computeOrderMilliGram,
  computeRetailPremiumTier,
  computeRetailStarPricing,
  computeRetailStarsQty,
  DEFAULT_PRICING_ALERT_MIN_DELTA_RUB,
  exceedsPricingAlertThreshold,
  feePercentToMarkup,
  markupToFeePercent,
  milliToGram,
  type PricingSnapshotComparison,
  QTY_LIMITS,
  STARS_MIN,
} from '@brainrotstore/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { api, dt, num } from '../api.js';
import { AdminIcon } from '../components/Icons.js';
import { useToast } from '../toast.js';

export interface SpendTierDraftInput {
  minSpentRub: string | number;
  starsFeePercent: string | number;
  premiumFeePercent: string | number;
}

interface SpendTierDraftRow {
  minSpentRub: string;
  starsFeePercent: string;
  premiumFeePercent: string;
}

interface SettingFieldRow {
  key: string;
  label: string;
  group: string;
  type: 'number' | 'number[]';
  hint?: string;
  default: number | number[];
  value: number | number[];
}

export interface StarPreviewRow {
  qty: number;
  label: string;
  baseRub: number;
  baseGram: number;
  retailRub: number;
  retailGram: number;
  retailStars: number;
  marginRub: number;
  marginPercent: number;
}

export interface PremiumPreviewRow {
  months: 3 | 6 | 12;
  label: string;
  baseRub: number;
  baseGram: number;
  retailRub: number;
  retailGram: number;
  retailStars: number;
  officialRub: number;
  marginRub: number;
  marginPercent: number;
}

export interface PricingPreviewResult {
  starsMarkup: number;
  premiumMarkup: number;
  starRows: StarPreviewRow[];
  premiumRows: PremiumPreviewRow[];
}

export interface SpendTierColumnMeta {
  index: number;
  minSpentRub: number;
  starsFeePercent: number;
  premiumFeePercent: number;
  starsMarkup: number;
  premiumMarkup: number;
}

export interface SpendTierCellPrice {
  minSpentRub: number;
  feePercent: number;
  markup: number;
  retailRub: number;
  retailGram: number;
  retailStars: number;
  savingsRub: number;
  savingsPercent: number;
}

export interface SpendTierPreviewMatrixRow {
  key: string;
  kind: 'stars' | 'premium';
  qtyOrMonths: number;
  label: string;
  wholesaleRub: number;
  wholesaleGram: number;
  baseTariffRub: number;
  baseTariffGram: number;
  baseTariffStars: number;
  tierCells: SpendTierCellPrice[];
}

export interface SpendTierPreviewMatrix {
  baseStarsFeePercent: number;
  basePremiumFeePercent: number;
  baseStarsMarkup: number;
  basePremiumMarkup: number;
  tiers: SpendTierColumnMeta[];
  rows: SpendTierPreviewMatrixRow[];
}

type PricingTab = 'base' | 'tiers' | 'packs';

const STAR_PREVIEW_QTYS = [1, 50, 100, 500, 1000] as const;
const PREMIUM_PREVIEW_MONTHS = [3, 6, 12] as const;

const PACK_SETTING_KEYS = new Set([
  'STAR_PACKS',
  'MIN_STARS',
  'MAX_STARS',
  'STARS_MIN',
  'STARS_MAX',
]);

function isPackOrLimitField(f: SettingFieldRow): boolean {
  // START_BLOCK_IS_PACK_OR_LIMIT_FIELD
  return (
    f.group === 'Витрина' ||
    PACK_SETTING_KEYS.has(f.key) ||
    f.key.includes('PACK') ||
    f.key.includes('STARS_LIMIT')
  );
  // END_BLOCK_IS_PACK_OR_LIMIT_FIELD
}

function parseDraftMultiplier(raw: string | undefined, fallback: number): number {
  // START_BLOCK_PARSE_DRAFT_MULTIPLIER
  if (!raw || !raw.trim()) return fallback;
  const parsed = Number(raw.replace(',', '.').trim());
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(10, Math.max(1, parsed));
  // END_BLOCK_PARSE_DRAFT_MULTIPLIER
}

function parseNonNegNumber(raw: string | number, fallback = 0): number {
  // START_BLOCK_PARSE_NON_NEG_NUMBER
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? Math.max(0, raw) : fallback;
  }
  const trimmed = raw.replace(',', '.').trim();
  if (!trimmed) return fallback;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : fallback;
  // END_BLOCK_PARSE_NON_NEG_NUMBER
}

function fmtMoney(n: number, digits = 2): string {
  // START_BLOCK_FMT_MONEY
  return n.toLocaleString('ru-RU', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  // END_BLOCK_FMT_MONEY
}

function fmtPct(n: number): string {
  // START_BLOCK_FMT_PCT
  return fmtMoney(n, n % 1 === 0 ? 0 : 2);
  // END_BLOCK_FMT_PCT
}

function fmtSignedMoney(n: number, digits = 2): string {
  // START_BLOCK_FMT_SIGNED_MONEY
  const abs = fmtMoney(Math.abs(n), digits);
  if (n > 0) return `+${abs}`;
  if (n < 0) return `−${abs}`;
  return abs;
  // END_BLOCK_FMT_SIGNED_MONEY
}

// START_CONTRACT: buildPricingPreviewRows
//   PURPOSE: Compute live Stars and Premium retail pricing and margin rows as the operator types X multipliers.
//   INPUTS: preview: AdminPricingPreviewDto, starsDraft: string | undefined, premiumDraft: string | undefined
//   OUTPUTS: PricingPreviewResult
//   SIDE_EFFECTS: none
//   LINKS: M-ADMIN
// END_CONTRACT: buildPricingPreviewRows
export function buildPricingPreviewRows(
  preview: AdminPricingPreviewDto,
  starsDraft?: string,
  premiumDraft?: string,
): PricingPreviewResult {
  // START_BLOCK_BUILD_PRICING_PREVIEW_ROWS
  const starsMarkup = parseDraftMultiplier(starsDraft, preview.starsMarkupMultiplier);
  const premiumMarkup = parseDraftMultiplier(premiumDraft, preview.premiumMarkupMultiplier);

  const unitStar = computeRetailStarPricing({
    baseStarGram: preview.fragment.baseStarGram,
    baseStarUsd: preview.fragment.baseStarUsd,
    gramRub: preview.fx.gramRub,
    usdRub: preview.fx.usdRub,
    markup: starsMarkup,
    paymentMethod: preview.paymentMethod,
  });

  const starRows: StarPreviewRow[] = STAR_PREVIEW_QTYS.map((qty) => {
    const baseRub = Number((unitStar.baseRub * qty).toFixed(2));
    const baseGram = Number((preview.fragment.baseStarGram * qty).toFixed(4));
    const retailRub = Number((unitStar.retailRub * qty).toFixed(2));
    const retailGram =
      qty === 1
        ? Number(unitStar.retailGram.toFixed(4))
        : milliToGram(computeOrderMilliGram(qty, unitStar.retailGram));
    const retailStars = computeRetailStarsQty(qty, starsMarkup);
    const marginRub = Number((retailRub - baseRub).toFixed(2));
    const marginPercent =
      baseRub > 0 ? Number((((retailRub - baseRub) / baseRub) * 100).toFixed(1)) : 0;
    return {
      qty,
      label: `${qty.toLocaleString('ru-RU')} Stars`,
      baseRub,
      baseGram,
      retailRub,
      retailGram,
      retailStars,
      marginRub,
      marginPercent,
    };
  });

  const premiumRows: PremiumPreviewRow[] = PREMIUM_PREVIEW_MONTHS.map((months) => {
    const fragTier = preview.fragment.premium[String(months) as '3' | '6' | '12'];
    const tier = computeRetailPremiumTier({
      months,
      baseGram: fragTier.baseGram,
      baseUsd: fragTier.baseUsd,
      discountPercent: fragTier.discountPercent,
      baseStarGram: preview.fragment.baseStarGram,
      gramRub: preview.fx.gramRub,
      usdRub: preview.fx.usdRub,
      markup: premiumMarkup,
      paymentMethod: preview.paymentMethod,
    });
    const marginRub = Number((tier.priceRub - tier.baseRub).toFixed(2));
    const marginPercent =
      tier.baseRub > 0
        ? Number((((tier.priceRub - tier.baseRub) / tier.baseRub) * 100).toFixed(1))
        : 0;
    return {
      months,
      label: `Premium ${months} мес.`,
      baseRub: tier.baseRub,
      baseGram: fragTier.baseGram,
      retailRub: tier.priceRub,
      retailGram: tier.priceGram,
      retailStars: tier.priceStars,
      officialRub: tier.officialRub,
      marginRub,
      marginPercent,
    };
  });

  return {
    starsMarkup,
    premiumMarkup,
    starRows,
    premiumRows,
  };
  // END_BLOCK_BUILD_PRICING_PREVIEW_ROWS
}

// START_CONTRACT: buildSpendTierPreviewMatrix
//   PURPOSE: Compute live client retail prices (RUB, GRAM, Stars) and savings vs Base Tariff across all draft Spend Tiers as the operator types.
//   INPUTS: preview: AdminPricingPreviewDto, tierDrafts: ReadonlyArray<SpendTierDraftInput>, starsBaseDraft?: string, premiumBaseDraft?: string
//   OUTPUTS: SpendTierPreviewMatrix
//   SIDE_EFFECTS: none
//   LINKS: M-ADMIN
// END_CONTRACT: buildSpendTierPreviewMatrix
export function buildSpendTierPreviewMatrix(
  preview: AdminPricingPreviewDto,
  tierDrafts: ReadonlyArray<SpendTierDraftInput>,
  starsBaseDraft?: string,
  premiumBaseDraft?: string,
): SpendTierPreviewMatrix {
  // START_BLOCK_BUILD_SPEND_TIER_PREVIEW_MATRIX
  const baseStarsMarkup = parseDraftMultiplier(starsBaseDraft, preview.starsMarkupMultiplier);
  const basePremiumMarkup = parseDraftMultiplier(premiumBaseDraft, preview.premiumMarkupMultiplier);
  const baseStarsFeePercent = markupToFeePercent(baseStarsMarkup);
  const basePremiumFeePercent = markupToFeePercent(basePremiumMarkup);

  const tiers: SpendTierColumnMeta[] = tierDrafts
    .map((draft, index) => {
      const minSpentRub = Number(parseNonNegNumber(draft.minSpentRub, 0).toFixed(2));
      const starsFeePercent = Math.min(
        900,
        Number(parseNonNegNumber(draft.starsFeePercent, 0).toFixed(2)),
      );
      const premiumFeePercent = Math.min(
        900,
        Number(parseNonNegNumber(draft.premiumFeePercent, 0).toFixed(2)),
      );
      return {
        index,
        minSpentRub,
        starsFeePercent,
        premiumFeePercent,
        starsMarkup: feePercentToMarkup(starsFeePercent),
        premiumMarkup: feePercentToMarkup(premiumFeePercent),
      };
    })
    .sort((a, b) => a.minSpentRub - b.minSpentRub || a.index - b.index);

  const baseStarUnit = computeRetailStarPricing({
    baseStarGram: preview.fragment.baseStarGram,
    baseStarUsd: preview.fragment.baseStarUsd,
    gramRub: preview.fx.gramRub,
    usdRub: preview.fx.usdRub,
    markup: baseStarsMarkup,
    paymentMethod: preview.paymentMethod,
  });

  const starMatrixRows: SpendTierPreviewMatrixRow[] = STAR_PREVIEW_QTYS.map((qty) => {
    const digits = qty === 1 ? 3 : 2;
    const wholesaleRub = Number((baseStarUnit.baseRub * qty).toFixed(digits));
    const wholesaleGram = Number((preview.fragment.baseStarGram * qty).toFixed(4));
    const baseTariffRub = Number((baseStarUnit.retailRub * qty).toFixed(digits));
    const baseTariffGram =
      qty === 1
        ? Number(baseStarUnit.retailGram.toFixed(4))
        : milliToGram(computeOrderMilliGram(qty, baseStarUnit.retailGram));
    const baseTariffStars = computeRetailStarsQty(qty, baseStarsMarkup);

    const tierCells: SpendTierCellPrice[] = tiers.map((tier) => {
      const tierUnit = computeRetailStarPricing({
        baseStarGram: preview.fragment.baseStarGram,
        baseStarUsd: preview.fragment.baseStarUsd,
        gramRub: preview.fx.gramRub,
        usdRub: preview.fx.usdRub,
        markup: tier.starsMarkup,
        paymentMethod: preview.paymentMethod,
      });
      const retailRub = Number((tierUnit.retailRub * qty).toFixed(digits));
      const retailGram =
        qty === 1
          ? Number(tierUnit.retailGram.toFixed(4))
          : milliToGram(computeOrderMilliGram(qty, tierUnit.retailGram));
      const retailStars = computeRetailStarsQty(qty, tier.starsMarkup);
      const savingsRub = Number((baseTariffRub - retailRub).toFixed(digits));
      const savingsPercent =
        baseTariffRub > 0
          ? Number((((baseTariffRub - retailRub) / baseTariffRub) * 100).toFixed(1))
          : 0;
      return {
        minSpentRub: tier.minSpentRub,
        feePercent: tier.starsFeePercent,
        markup: tier.starsMarkup,
        retailRub,
        retailGram,
        retailStars,
        savingsRub,
        savingsPercent,
      };
    });

    return {
      key: `stars-${qty}`,
      kind: 'stars',
      qtyOrMonths: qty,
      label: qty === 1 ? '1 Star' : `${qty.toLocaleString('ru-RU')} Stars`,
      wholesaleRub,
      wholesaleGram,
      baseTariffRub,
      baseTariffGram,
      baseTariffStars,
      tierCells,
    };
  });

  const premiumMatrixRows: SpendTierPreviewMatrixRow[] = PREMIUM_PREVIEW_MONTHS.map((months) => {
    const fragTier = preview.fragment.premium[String(months) as '3' | '6' | '12'];
    const basePrem = computeRetailPremiumTier({
      months,
      baseGram: fragTier.baseGram,
      baseUsd: fragTier.baseUsd,
      discountPercent: fragTier.discountPercent,
      baseStarGram: preview.fragment.baseStarGram,
      gramRub: preview.fx.gramRub,
      usdRub: preview.fx.usdRub,
      markup: basePremiumMarkup,
      paymentMethod: preview.paymentMethod,
    });

    const tierCells: SpendTierCellPrice[] = tiers.map((tier) => {
      const tierPrem = computeRetailPremiumTier({
        months,
        baseGram: fragTier.baseGram,
        baseUsd: fragTier.baseUsd,
        discountPercent: fragTier.discountPercent,
        baseStarGram: preview.fragment.baseStarGram,
        gramRub: preview.fx.gramRub,
        usdRub: preview.fx.usdRub,
        markup: tier.premiumMarkup,
        paymentMethod: preview.paymentMethod,
      });
      const savingsRub = Number((basePrem.priceRub - tierPrem.priceRub).toFixed(2));
      const savingsPercent =
        basePrem.priceRub > 0
          ? Number((((basePrem.priceRub - tierPrem.priceRub) / basePrem.priceRub) * 100).toFixed(1))
          : 0;
      return {
        minSpentRub: tier.minSpentRub,
        feePercent: tier.premiumFeePercent,
        markup: tier.premiumMarkup,
        retailRub: tierPrem.priceRub,
        retailGram: tierPrem.priceGram,
        retailStars: tierPrem.priceStars,
        savingsRub,
        savingsPercent,
      };
    });

    return {
      key: `premium-${months}`,
      kind: 'premium',
      qtyOrMonths: months,
      label: `Premium ${months} мес.`,
      wholesaleRub: basePrem.baseRub,
      wholesaleGram: fragTier.baseGram,
      baseTariffRub: basePrem.priceRub,
      baseTariffGram: basePrem.priceGram,
      baseTariffStars: basePrem.priceStars,
      tierCells,
    };
  });

  return {
    baseStarsFeePercent,
    basePremiumFeePercent,
    baseStarsMarkup,
    basePremiumMarkup,
    tiers,
    rows: [...starMatrixRows, ...premiumMatrixRows],
  };
  // END_BLOCK_BUILD_SPEND_TIER_PREVIEW_MATRIX
}

export interface LiveComparisonPreview extends PricingSnapshotComparison {
  lockedAt: string;
  lockedByTelegramId: number | null;
  liveCheckedAt: string;
  liveGramRub: number;
  liveUsdRub: number;
  liveGramUsd: number;
  liveSource: 'live' | 'cache' | 'BootstrapFallback';
  dismissed: boolean;
}

// START_CONTRACT: buildLiveComparisonRows
//   PURPOSE: Compute locked vs live retail RUB and Fragment GRAM deltas dynamically using current draft X multipliers.
//   INPUTS: preview: AdminPricingPreviewDto, starsDraft?: string, premiumDraft?: string
//   OUTPUTS: LiveComparisonPreview | null
//   SIDE_EFFECTS: none
//   LINKS: M-ADMIN
// END_CONTRACT: buildLiveComparisonRows
export function buildLiveComparisonRows(
  preview: AdminPricingPreviewDto,
  starsDraft?: string,
  premiumDraft?: string,
): LiveComparisonPreview | null {
  // START_BLOCK_BUILD_LIVE_COMPARISON_ROWS
  const lc = preview.liveComparison;
  if (!lc) return null;

  const starsMarkup = parseDraftMultiplier(starsDraft, preview.starsMarkupMultiplier);
  const premiumMarkup = parseDraftMultiplier(premiumDraft, preview.premiumMarkupMultiplier);

  const comp = comparePricingSnapshots({
    locked: {
      baseStarGram: preview.fragment.baseStarGram,
      baseStarUsd: preview.fragment.baseStarUsd,
      premium: {
        3: preview.fragment.premium['3'],
        6: preview.fragment.premium['6'],
        12: preview.fragment.premium['12'],
      },
      gramRub: preview.fx.gramRub,
      usdRub: preview.fx.usdRub,
      gramUsd: preview.fx.gramUsd,
    },
    live: {
      baseStarGram: lc.liveFragment.baseStarGram,
      baseStarUsd: lc.liveFragment.baseStarUsd,
      premium: {
        3: lc.liveFragment.premium['3'],
        6: lc.liveFragment.premium['6'],
        12: lc.liveFragment.premium['12'],
      },
      gramRub: lc.liveFx.gramRub,
      usdRub: lc.liveFx.usdRub,
      gramUsd: lc.liveFx.gramUsd,
    },
    starsMarkup,
    premiumMarkup,
    paymentMethod: preview.paymentMethod,
  });

  return {
    ...comp,
    lockedAt: lc.lockedAt,
    lockedByTelegramId: lc.lockedByTelegramId ?? null,
    liveCheckedAt: lc.liveCheckedAt,
    liveGramRub: lc.liveFx.gramRub,
    liveUsdRub: lc.liveFx.usdRub,
    liveGramUsd: lc.liveFx.gramUsd,
    liveSource: lc.liveFragment.source,
    dismissed: lc.dismissed,
  };
  // END_BLOCK_BUILD_LIVE_COMPARISON_ROWS
}

// START_CONTRACT: Pricing
//   PURPOSE: Render the dedicated 3-tab Pricing & Tariffs page (Base Markups & Fragment Rate, Spend-Volume Tiers with Live Resulting Prices Matrix, and Storefront Packs).
//   INPUTS: none
//   OUTPUTS: React element
//   SIDE_EFFECTS: Queries and mutates /admin/pricing-preview, /admin/pricing/spend-tiers, and /admin/settings
//   LINKS: M-ADMIN
// END_CONTRACT: Pricing
export function Pricing() {
  // START_BLOCK_PRICING_PAGE
  const qc = useQueryClient();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const rawTab = searchParams.get('tab');
  const activeTab: PricingTab =
    rawTab === 'tiers' || rawTab === 'packs' || rawTab === 'base' ? rawTab : 'base';

  const setTab = (next: PricingTab) => {
    const nextParams = new URLSearchParams(searchParams);
    if (next === 'base') {
      nextParams.delete('tab');
    } else {
      nextParams.set('tab', next);
    }
    setSearchParams(nextParams, { replace: true });
  };

  const { data: settingsData, isLoading: settingsLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<SettingFieldRow[]>('/admin/settings'),
  });

  const { data: previewData, isLoading: previewLoading } = useQuery({
    queryKey: ['pricing-preview'],
    queryFn: () => api.get<AdminPricingPreviewDto>('/admin/pricing-preview'),
  });

  const [draft, setDraft] = useState<Record<string, string>>({});
  const [spendTierRows, setSpendTierRows] = useState<SpendTierDraftRow[]>([]);

  useEffect(() => {
    if (!settingsData) return;
    setDraft((prev) => {
      const next = { ...prev };
      for (const f of settingsData) {
        next[f.key] = Array.isArray(f.value) ? f.value.join(', ') : String(f.value);
      }
      return next;
    });
  }, [settingsData]);

  useEffect(() => {
    if (!previewData) return;
    setDraft((prev) => ({
      STARS_MARKUP_MULTIPLIER:
        prev['STARS_MARKUP_MULTIPLIER'] ?? String(previewData.starsMarkupMultiplier),
      PREMIUM_MARKUP_MULTIPLIER:
        prev['PREMIUM_MARKUP_MULTIPLIER'] ?? String(previewData.premiumMarkupMultiplier),
      ...prev,
    }));
    setSpendTierRows(
      (previewData.spendTiers ?? []).map((t) => ({
        minSpentRub: String(t.minSpentRub),
        starsFeePercent: String(t.starsFeePercent),
        premiumFeePercent: String(t.premiumFeePercent),
      })),
    );
  }, [previewData]);

  const saveSetting = useMutation({
    mutationFn: ({ key, value }: { key: string; value: number | number[] }) =>
      api.put<{ ok: boolean; reason?: string }>('/admin/settings', { key, value }),
    onSuccess: (r) => {
      toast(r.ok ? 'Сохранено' : (r.reason ?? 'Не удалось сохранить'));
      if (r.ok) {
        void qc.invalidateQueries({ queryKey: ['settings'] });
        void qc.invalidateQueries({ queryKey: ['pricing-preview'] });
      }
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Ошибка сохранения параметра'),
  });

  const refreshFragment = useMutation({
    mutationFn: () => api.post<AdminPricingPreviewDto>('/admin/pricing-preview/refresh', {}),
    onSuccess: (fresh) => {
      qc.setQueryData(['pricing-preview'], fresh);
      toast('Свежий рыночный курс загружен для сравнения (цены на витрине не изменены)');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Не удалось проверить свежий курс'),
  });

  const applyLivePricing = useMutation({
    mutationFn: () => api.post<AdminPricingPreviewDto>('/admin/pricing/apply-live', {}),
    onSuccess: (fresh) => {
      qc.setQueryData(['pricing-preview'], fresh);
      toast('Актуальный курс зафиксирован и применён к витрине магазина');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Не удалось применить курс'),
  });

  const dismissCandidate = useMutation({
    mutationFn: () => api.post<AdminPricingPreviewDto>('/admin/pricing/dismiss-candidate', {}),
    onSuccess: (fresh) => {
      qc.setQueryData(['pricing-preview'], fresh);
      toast('Оставлен текущий зафиксированный курс');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Не удалось сохранить решение'),
  });

  const saveSpendTiers = useMutation({
    mutationFn: (tiers: AdminSpendCommissionTierDto[]) =>
      api.put<{ tiers: AdminSpendCommissionTierDto[] }>('/admin/pricing/spend-tiers', { tiers }),
    onSuccess: (res) => {
      toast('Ступени сниженной комиссии за оборот сохранены');
      setSpendTierRows(
        (res.tiers ?? []).map((t) => ({
          minSpentRub: String(t.minSpentRub),
          starsFeePercent: String(t.starsFeePercent),
          premiumFeePercent: String(t.premiumFeePercent),
        })),
      );
      void qc.invalidateQueries({ queryKey: ['pricing-preview'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Ошибка сохранения ступеней'),
  });

  const handleSaveSpendTiers = () => {
    const parsed: AdminSpendCommissionTierDto[] = [];
    for (let i = 0; i < spendTierRows.length; i++) {
      const row = spendTierRows[i]!;
      const minSpentRub = Number(row.minSpentRub.replace(',', '.').trim());
      const starsFeePercent = Number(row.starsFeePercent.replace(',', '.').trim());
      const premiumFeePercent = Number(row.premiumFeePercent.replace(',', '.').trim());
      if (!Number.isFinite(minSpentRub) || minSpentRub <= 0) {
        return toast(`Строка ${i + 1}: укажите порог покупок больше 0 ₽`);
      }
      if (
        !Number.isFinite(starsFeePercent) ||
        starsFeePercent < 0 ||
        starsFeePercent > 900 ||
        !Number.isFinite(premiumFeePercent) ||
        premiumFeePercent < 0 ||
        premiumFeePercent > 900
      ) {
        return toast(`Строка ${i + 1}: процент комиссии должен быть от 0% до 900%`);
      }
      parsed.push({ minSpentRub, starsFeePercent, premiumFeePercent });
    }
    saveSpendTiers.mutate(parsed);
  };

  const submitMultiplier = (key: 'STARS_MARKUP_MULTIPLIER' | 'PREMIUM_MARKUP_MULTIPLIER') => {
    const raw = draft[key] ?? '';
    const n = Number(raw.replace(',', '.').trim());
    if (!Number.isFinite(n) || n < 1 || n > 10) {
      return toast('Множитель X должен быть числом от 1.00 до 10.00');
    }
    saveSetting.mutate({ key, value: n });
  };

  const submitAlertMinDelta = () => {
    const raw = draft['PRICING_ALERT_MIN_DELTA_RUB'] ?? '';
    const n = Number(raw.replace(',', '.').trim());
    if (!Number.isFinite(n) || n < 0 || n > 100_000) {
      return toast('Порог уведомлений должен быть числом от 0 до 100 000 ₽');
    }
    saveSetting.mutate({ key: 'PRICING_ALERT_MIN_DELTA_RUB', value: Number(n.toFixed(2)) });
  };

  const submitField = (f: SettingFieldRow) => {
    const raw = draft[f.key] ?? '';
    if (f.type === 'number[]') {
      const arr = raw
        .split(',')
        .map((x) => Number(x.trim()))
        .filter((n) => Number.isFinite(n) && n > 0);
      if (!arr.length) return toast('Введите положительные числа через запятую');
      saveSetting.mutate({ key: f.key, value: arr });
    } else {
      const n = Number(raw.replace(',', '.').trim());
      if (!Number.isFinite(n) || n < 0) return toast('Введите неотрицательное число');
      saveSetting.mutate({ key: f.key, value: n });
    }
  };

  if ((settingsLoading && !settingsData) || (previewLoading && !previewData)) {
    return <div className="muted">Загрузка раздела цен и тарифов…</div>;
  }

  const starsField = settingsData?.find((f) => f.key === 'STARS_MARKUP_MULTIPLIER');
  const premiumField = settingsData?.find((f) => f.key === 'PREMIUM_MARKUP_MULTIPLIER');
  const alertMinDeltaField = settingsData?.find((f) => f.key === 'PRICING_ALERT_MIN_DELTA_RUB');
  const packFields = (settingsData ?? []).filter(isPackOrLimitField);

  const savedStarsVal =
    typeof starsField?.value === 'number'
      ? starsField.value
      : (previewData?.starsMarkupMultiplier ?? 1.1);
  const savedPremiumVal =
    typeof premiumField?.value === 'number'
      ? premiumField.value
      : (previewData?.premiumMarkupMultiplier ?? 1.1);
  const savedAlertMinDeltaVal =
    typeof alertMinDeltaField?.value === 'number'
      ? alertMinDeltaField.value
      : DEFAULT_PRICING_ALERT_MIN_DELTA_RUB;

  const starsDraftStr = draft['STARS_MARKUP_MULTIPLIER'] ?? String(savedStarsVal);
  const premiumDraftStr = draft['PREMIUM_MARKUP_MULTIPLIER'] ?? String(savedPremiumVal);
  const alertMinDeltaDraftStr =
    draft['PRICING_ALERT_MIN_DELTA_RUB'] ?? String(savedAlertMinDeltaVal);
  const effectiveAlertMinDelta = parseNonNegNumber(
    alertMinDeltaDraftStr,
    savedAlertMinDeltaVal,
  );
  const alertMinDeltaChanged =
    alertMinDeltaDraftStr.trim() !== String(savedAlertMinDeltaVal);

  const liveStarsMult = parseDraftMultiplier(starsDraftStr, savedStarsVal);
  const livePremiumMult = parseDraftMultiplier(premiumDraftStr, savedPremiumVal);
  const liveStarsFeePct = markupToFeePercent(liveStarsMult);
  const livePremiumFeePct = markupToFeePercent(livePremiumMult);

  const starsChanged = starsDraftStr.trim() !== String(savedStarsVal);
  const premiumChanged = premiumDraftStr.trim() !== String(savedPremiumVal);

  const computedPreview = previewData
    ? buildPricingPreviewRows(previewData, starsDraftStr, premiumDraftStr)
    : null;

  const tierMatrix = previewData
    ? buildSpendTierPreviewMatrix(previewData, spendTierRows, starsDraftStr, premiumDraftStr)
    : null;

  const liveComparison = previewData
    ? buildLiveComparisonRows(previewData, starsDraftStr, premiumDraftStr)
    : null;

  const sourceBadgeClass =
    previewData?.fragment.source === 'live'
      ? 'chip ok'
      : previewData?.fragment.source === 'cache'
        ? 'chip info'
        : 'chip AWAITING_PAYMENT';

  const sourceLabel =
    previewData?.fragment.source === 'live'
      ? 'Зафиксирован'
      : previewData?.fragment.source === 'cache'
        ? 'Кэш БД'
        : 'Резервный курс';

  const parsedStarPacksPreview = (draft['STAR_PACKS'] ?? '50, 100, 500, 1000')
    .split(',')
    .map((x) => Math.floor(Number(x.trim())))
    .filter((n) => Number.isFinite(n) && n > 0);

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Цены и тарифы</h1>
          <div className="page-subtitle">
            Фиксация базового курса витрины, ежедневная сверка котировок Fragment и управление наценками магазина
          </div>
        </div>
        {previewData && (
          <button
            type="button"
            className="btn ghost"
            disabled={refreshFragment.isPending}
            onClick={() => refreshFragment.mutate()}
          >
            <AdminIcon name="refresh" size={14} />
            <span>
              {refreshFragment.isPending ? 'Проверка курса…' : 'Проверить свежий курс'}
            </span>
          </button>
        )}
      </div>

      <div className="tabs">
        <button
          type="button"
          className={`tab-btn ${activeTab === 'base' ? 'active' : ''}`}
          onClick={() => setTab('base')}
        >
          Базовые наценки и курс Fragment
        </button>
        <button
          type="button"
          className={`tab-btn ${activeTab === 'tiers' ? 'active' : ''}`}
          onClick={() => setTab('tiers')}
        >
          Ступени за объём покупок ({spendTierRows.length})
        </button>
        <button
          type="button"
          className={`tab-btn ${activeTab === 'packs' ? 'active' : ''}`}
          onClick={() => setTab('packs')}
        >
          Пакеты витрины ({packFields.length})
        </button>
      </div>

      {activeTab === 'base' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {previewData && (
            <div className="grid cols-4">
              <div className="kpi-card green">
                <div className="kpi-label">Режим витрины</div>
                <div className="kpi-value" style={{ marginTop: 8 }}>
                  <span className={sourceBadgeClass}>
                    <AdminIcon name="lock" size={12} style={{ marginRight: 5 }} />
                    {sourceLabel}
                  </span>
                </div>
                <div className="kpi-sub">
                  От: {dt(liveComparison?.lockedAt ?? previewData.fragment.updatedAt)}
                </div>
              </div>
              <div className="kpi-card cyan">
                <div className="kpi-label">Зафиксированный курс GRAM</div>
                <div className="kpi-value mono">{fmtMoney(previewData.fx.gramRub)} ₽</div>
                <div className="kpi-sub mono">
                  {liveComparison && liveComparison.fxChanged
                    ? `Рынок сейчас: ${fmtMoney(liveComparison.liveGramRub)} ₽ (${fmtSignedMoney(liveComparison.gramRubDelta)} ₽)`
                    : `$${fmtMoney(previewData.fx.gramUsd, 3)} за 1 GRAM`}
                </div>
              </div>
              <div className="kpi-card blue">
                <div className="kpi-label">Зафиксированный USD / RUB</div>
                <div className="kpi-value mono">{fmtMoney(previewData.fx.usdRub)} ₽</div>
                <div className="kpi-sub">
                  {liveComparison && Math.abs(liveComparison.usdRubDelta) >= 0.01
                    ? `Рынок сейчас: ${fmtMoney(liveComparison.liveUsdRub)} ₽ (${fmtSignedMoney(liveComparison.usdRubDelta)} ₽)`
                    : `Расчётный метод: ${previewData.paymentMethod.toUpperCase()}`}
                </div>
              </div>
              <div className="kpi-card amber">
                <div className="kpi-label">База витрины 1 Star</div>
                <div className="kpi-value mono">
                  {fmtMoney(previewData.fragment.baseStarGram, 4)} GRAM
                </div>
                <div className="kpi-sub mono">
                  ${fmtMoney(previewData.fragment.baseStarUsd, 4)} ·{' '}
                  {fmtMoney(previewData.fragment.baseStarGram * previewData.fx.gramRub, 3)} ₽
                </div>
              </div>
            </div>
          )}

          {previewData && liveComparison && (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div
                style={{
                  padding: '16px 20px',
                  borderBottom: '1px solid var(--hair)',
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                }}
              >
                <div>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <h2 style={{ margin: 0 }}>Ежедневная сверка курса и оптовых цен Fragment</h2>
                    {!liveComparison.hasChanges && (
                      <span className="chip ok">Курс на витрине совпадает с рынком</span>
                    )}
                    {liveComparison.fragmentGramChanged && (
                      <span className="chip AWAITING_PAYMENT">
                        Изменилась цена в GRAM на Fragment
                      </span>
                    )}
                    {liveComparison.fxChanged && (
                      <span className="chip info">Изменился суточный курс RUB</span>
                    )}
                    {liveComparison.hasChanges && liveComparison.dismissed && (
                      <span className="chip">Оставлен текущий курс</span>
                    )}
                  </div>
                  <div className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>
                    Зафиксирован: <b>{dt(liveComparison.lockedAt)}</b> · Сверка рынка:{' '}
                    <b>{dt(liveComparison.liveCheckedAt)}</b> · 1 GRAM:{' '}
                    <span className="mono">{fmtMoney(previewData.fx.gramRub)} ₽</span> →{' '}
                    <span className="mono">{fmtMoney(liveComparison.liveGramRub)} ₽</span> (
                    <span className="mono">
                      {fmtSignedMoney(liveComparison.gramRubDelta)} ₽ /{' '}
                      {fmtSignedMoney(liveComparison.gramRubDeltaPercent)}%
                    </span>
                    )
                  </div>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                  <button
                    type="button"
                    className="btn sm ghost"
                    disabled={refreshFragment.isPending}
                    onClick={() => refreshFragment.mutate()}
                  >
                    <AdminIcon name="refresh" size={13} />
                    <span>{refreshFragment.isPending ? 'Сверка…' : 'Проверить свежий курс'}</span>
                  </button>
                  <button
                    type="button"
                    className="btn sm"
                    disabled={
                      !liveComparison.hasChanges ||
                      liveComparison.dismissed ||
                      dismissCandidate.isPending
                    }
                    onClick={() => dismissCandidate.mutate()}
                  >
                    <AdminIcon name="close" size={13} />
                    <span>Оставить текущий курс</span>
                  </button>
                  <button
                    type="button"
                    className="btn sm primary"
                    disabled={!liveComparison.hasChanges || applyLivePricing.isPending}
                    onClick={() => applyLivePricing.mutate()}
                  >
                    <AdminIcon name="check" size={13} />
                    <span>
                      {applyLivePricing.isPending
                        ? 'Применение…'
                        : 'Применить актуальный курс'}
                    </span>
                  </button>
                </div>
              </div>

              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Позиция</th>
                      <th>Оптовая база GRAM</th>
                      <th>Сейчас в витрине (₽)</th>
                      <th>По свежему курсу (₽)</th>
                      <th>Разница в рублях</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      {
                        key: 'star-1',
                        icon: 'star' as const,
                        label: '1 Star',
                        gramChanged: Math.abs(liveComparison.star1.deltaBaseGram) >= 0.000001,
                        lockedGram: fmtMoney(liveComparison.star1.lockedBaseGram, 4),
                        liveGram: fmtMoney(liveComparison.star1.liveBaseGram, 4),
                        lockedRub: liveComparison.star1.lockedRub,
                        liveRub: liveComparison.star1.liveRub,
                        deltaRub: liveComparison.star1.deltaRub,
                        deltaPercent: liveComparison.star1.deltaPercent,
                        rubDigits: 3,
                      },
                      {
                        key: 'stars-100',
                        icon: 'star' as const,
                        label: '100 Stars',
                        gramChanged: Math.abs(liveComparison.star1.deltaBaseGram) >= 0.000001,
                        lockedGram: fmtMoney(liveComparison.star1.lockedBaseGram * 100, 2),
                        liveGram: fmtMoney(liveComparison.star1.liveBaseGram * 100, 2),
                        lockedRub: liveComparison.stars100.lockedRub,
                        liveRub: liveComparison.stars100.liveRub,
                        deltaRub: liveComparison.stars100.deltaRub,
                        deltaPercent: liveComparison.stars100.deltaPercent,
                        rubDigits: 2,
                      },
                      ...liveComparison.premium.map((p) => ({
                        key: `prem-${p.months}`,
                        icon: 'premium' as const,
                        label: `Premium ${p.months} мес.`,
                        gramChanged: Math.abs(p.deltaBaseGram) >= 0.0001,
                        lockedGram: fmtMoney(p.lockedBaseGram, 2),
                        liveGram: fmtMoney(p.liveBaseGram, 2),
                        lockedRub: p.lockedRub,
                        liveRub: p.liveRub,
                        deltaRub: p.deltaRub,
                        deltaPercent: p.deltaPercent,
                        rubDigits: 2,
                      })),
                    ].map((row) => {
                      const absThreshold = row.rubDigits === 3 ? 0.001 : 0.01;
                      const deltaColor =
                        Math.abs(row.deltaRub) < absThreshold
                          ? 'var(--ink-soft)'
                          : row.deltaRub > 0
                            ? 'var(--amber)'
                            : 'var(--green)';
                      return (
                        <tr key={row.key}>
                          <td style={{ fontWeight: 600 }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <AdminIcon name={row.icon} size={14} />
                              <span>{row.label}</span>
                            </span>
                          </td>
                          <td className="mono" style={{ color: 'var(--ink-soft)' }}>
                            {row.gramChanged ? (
                              <span style={{ color: 'var(--amber)', fontWeight: 600 }}>
                                {row.lockedGram} → {row.liveGram} GRAM
                              </span>
                            ) : (
                              <span>{row.lockedGram} GRAM</span>
                            )}
                          </td>
                          <td className="mono" style={{ fontWeight: 600 }}>
                            {fmtMoney(row.lockedRub, row.rubDigits)} ₽
                          </td>
                          <td className="mono" style={{ fontWeight: 600 }}>
                            {fmtMoney(row.liveRub, row.rubDigits)} ₽
                          </td>
                          <td className="mono" style={{ color: deltaColor, fontWeight: 600 }}>
                            {Math.abs(row.deltaRub) < absThreshold ? (
                              <span>Без изменений</span>
                            ) : (
                              <span>
                                {fmtSignedMoney(row.deltaRub, row.rubDigits)} ₽ (
                                {fmtSignedMoney(row.deltaPercent, 2)}%)
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div
                style={{
                  padding: '14px 20px',
                  borderTop: '1px solid var(--hair)',
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                }}
              >
                <div style={{ flex: '1 1 320px' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                    <AdminIcon name="bell" size={14} />
                    <span style={{ fontWeight: 600 }}>
                      Порог уведомлений в бота и админку (₽)
                    </span>
                    {exceedsPricingAlertThreshold(liveComparison, effectiveAlertMinDelta) ? (
                      <span className="chip AWAITING_PAYMENT">
                        Превышает порог (&gt; {fmtMoney(effectiveAlertMinDelta, 2)} ₽)
                      </span>
                    ) : (
                      <span className="chip">
                        В пределах порога (≤ {fmtMoney(effectiveAlertMinDelta, 2)} ₽ — без спама)
                      </span>
                    )}
                  </div>
                  <div className="muted" style={{ fontSize: 12.5, marginTop: 3 }}>
                    Оповещения об изменении курса приходят в бота и админку, только если разница итоговой цены <b>100 ⭐</b> или любого тарифа <b>Premium</b> строго больше указанной суммы в рублях.
                  </div>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="number"
                      min={0}
                      max={100000}
                      step="0.1"
                      className="mono"
                      style={{ width: 110 }}
                      value={alertMinDeltaDraftStr}
                      onChange={(e) =>
                        setDraft((prev) => ({
                          ...prev,
                          PRICING_ALERT_MIN_DELTA_RUB: e.target.value,
                        }))
                      }
                    />
                    <span className="muted mono">₽</span>
                  </div>
                  <button
                    type="button"
                    className="btn sm primary"
                    disabled={!alertMinDeltaChanged || saveSetting.isPending}
                    onClick={submitAlertMinDelta}
                  >
                    <AdminIcon name="check" size={13} />
                    <span>Сохранить порог</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="card">
            <div style={{ marginBottom: 12 }}>
              <h2 style={{ marginBottom: 4 }}>Базовые наценки магазина</h2>
              <div className="muted" style={{ fontSize: 13 }}>
                Укажите множитель <b>X</b> (от 1.00 до 10.00) или эквивалентную комиссию в <b>%</b> поверх оптовой цены Fragment. Поля синхронизируются мгновенно.
              </div>
            </div>

            {[
              {
                key: 'STARS_MARKUP_MULTIPLIER' as const,
                title: starsField?.label ?? 'Множитель наценки Stars (X)',
                hint:
                  starsField?.hint ??
                  'Применяется ко всем покупкам Telegram Stars для клиентов без персонального тарифа или ступени',
                defVal: starsField?.default ?? 1.1,
                draftVal: starsDraftStr,
                feePct: liveStarsFeePct,
                changed: starsChanged,
                icon: 'star' as const,
              },
              {
                key: 'PREMIUM_MARKUP_MULTIPLIER' as const,
                title: premiumField?.label ?? 'Множитель наценки Premium (X)',
                hint:
                  premiumField?.hint ??
                  'Применяется к подпискам Telegram Premium (3 / 6 / 12 мес.) поверх оптовой цены Fragment',
                defVal: premiumField?.default ?? 1.1,
                draftVal: premiumDraftStr,
                feePct: livePremiumFeePct,
                changed: premiumChanged,
                icon: 'premium' as const,
              },
            ].map((item, idx) => (
              <div
                key={item.key}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 14,
                  padding: '14px 0',
                  borderTop: idx > 0 ? '1px solid var(--hair)' : 'none',
                }}
              >
                <div style={{ flex: '1 1 240px', minWidth: 0 }}>
                  <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <AdminIcon name={item.icon} size={15} />
                    <span>{item.title}</span>
                    <span className="chip info mono">Комиссия +{fmtPct(item.feePct)}%</span>
                  </div>
                  <div className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>
                    {item.hint} · по умолчанию: <span className="mono">{String(item.defVal)}</span>
                  </div>
                </div>

                <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
                  <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
                    <span className="muted" style={{ fontSize: 12 }}>
                      X:
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      min="1"
                      max="10"
                      className="mono"
                      style={{ width: 96 }}
                      aria-label={`${item.title} — множитель X`}
                      value={item.draftVal}
                      onChange={(e) => setDraft({ ...draft, [item.key]: e.target.value })}
                    />
                  </div>
                  <span className="muted" style={{ fontSize: 12 }}>
                    или
                  </span>
                  <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      max="900"
                      className="mono"
                      style={{ width: 96 }}
                      aria-label={`${item.title} — комиссия в процентах`}
                      value={String(item.feePct)}
                      onChange={(e) => {
                        const rawPct = e.target.value;
                        if (!rawPct.trim()) {
                          setDraft({ ...draft, [item.key]: '1' });
                          return;
                        }
                        const pct = Number(rawPct.replace(',', '.'));
                        if (Number.isFinite(pct)) {
                          setDraft({
                            ...draft,
                            [item.key]: String(feePercentToMarkup(pct)),
                          });
                        }
                      }}
                    />
                    <span className="muted" style={{ fontSize: 12 }}>
                      %
                    </span>
                  </div>
                  <button
                    type="button"
                    className="btn sm primary"
                    disabled={!item.changed || saveSetting.isPending}
                    onClick={() => submitMultiplier(item.key)}
                  >
                    <AdminIcon name="check" size={13} />
                    <span>Сохранить</span>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {previewData && computedPreview && (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div
                style={{
                  padding: '16px 20px',
                  borderBottom: '1px solid var(--hair)',
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                }}
              >
                <div>
                  <h2 style={{ margin: 0 }}>Калькулятор розничных цен и чистой маржи</h2>
                  <div className="muted" style={{ fontSize: 12.5, marginTop: 3 }}>
                    Живой пересчёт при вводе множителя X · 1 GRAM ={' '}
                    <span className="mono">{fmtMoney(previewData.fx.gramRub)} ₽</span> ($
                    <span className="mono">{fmtMoney(previewData.fx.gramUsd, 3)}</span>) · 1 USD ={' '}
                    <span className="mono">{fmtMoney(previewData.fx.usdRub)} ₽</span>
                  </div>
                </div>
              </div>

              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Позиция</th>
                      <th>Себестоимость Fragment</th>
                      <th>Множитель X</th>
                      <th>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <span>Цена продажи (₽ / GRAM /</span>
                          <AdminIcon name="star" size={13} />
                          <span>)</span>
                        </span>
                      </th>
                      <th>Чистая маржа</th>
                    </tr>
                  </thead>
                  <tbody>
                    {computedPreview.starRows.map((row) => (
                      <tr key={`s-${row.qty}`}>
                        <td style={{ fontWeight: 600 }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <AdminIcon name="star" size={14} />
                            <span>{row.qty === 1 ? '1 Star' : row.label}</span>
                          </span>
                        </td>
                        <td className="mono" style={{ color: 'var(--ink-soft)' }}>
                          {fmtMoney(row.baseRub, row.qty === 1 ? 3 : 2)} ₽ ·{' '}
                          {fmtMoney(row.baseGram, 4)} GRAM
                        </td>
                        <td className="mono" style={{ color: 'var(--accent)', fontWeight: 600 }}>
                          ×{fmtMoney(computedPreview.starsMarkup, 2)}
                        </td>
                        <td className="mono" style={{ fontWeight: 600 }}>
                          <span>{fmtMoney(row.retailRub, row.qty === 1 ? 3 : 2)} ₽</span>
                          <span style={{ color: 'var(--ink-soft)', fontWeight: 500 }}>
                            {' '}
                            · {fmtMoney(row.retailGram, 4)} GRAM ·{' '}
                          </span>
                          <span
                            style={{
                              color: 'var(--ink-soft)',
                              fontWeight: 500,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                            }}
                          >
                            <span>{num(row.retailStars)}</span>
                            <AdminIcon name="star" size={13} />
                          </span>
                        </td>
                        <td className="mono" style={{ color: 'var(--green)', fontWeight: 600 }}>
                          +{fmtMoney(row.marginRub, row.qty === 1 ? 3 : 2)} ₽ (+
                          {fmtMoney(row.marginPercent, 1)}%)
                        </td>
                      </tr>
                    ))}
                    {computedPreview.premiumRows.map((row) => (
                      <tr
                        key={`p-${row.months}`}
                        style={{ background: 'rgba(130, 148, 255, 0.04)' }}
                      >
                        <td style={{ fontWeight: 600 }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <AdminIcon name="premium" size={14} />
                            <span>{row.label}</span>
                          </span>
                        </td>
                        <td className="mono" style={{ color: 'var(--ink-soft)' }}>
                          {fmtMoney(row.baseRub)} ₽ · {fmtMoney(row.baseGram, 2)} GRAM
                        </td>
                        <td className="mono" style={{ color: 'var(--accent)', fontWeight: 600 }}>
                          ×{fmtMoney(computedPreview.premiumMarkup, 2)}
                        </td>
                        <td className="mono" style={{ fontWeight: 600 }}>
                          <span>{fmtMoney(row.retailRub, 0)} ₽ </span>
                          <span
                            style={{
                              textDecoration: 'line-through',
                              color: 'var(--ink-faint)',
                              fontWeight: 400,
                              marginRight: 4,
                            }}
                          >
                            {fmtMoney(row.officialRub, 0)} ₽
                          </span>
                          <span style={{ color: 'var(--ink-soft)', fontWeight: 500 }}>
                            · {fmtMoney(row.retailGram, 2)} GRAM ·{' '}
                          </span>
                          <span
                            style={{
                              color: 'var(--ink-soft)',
                              fontWeight: 500,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                            }}
                          >
                            <span>{num(row.retailStars)}</span>
                            <AdminIcon name="star" size={13} />
                          </span>
                        </td>
                        <td className="mono" style={{ color: 'var(--green)', fontWeight: 600 }}>
                          +{fmtMoney(row.marginRub)} ₽ (+{fmtMoney(row.marginPercent, 1)}%)
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'tiers' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                marginBottom: 14,
              }}
            >
              <div>
                <h2 style={{ marginBottom: 4 }}>
                  Шкала сниженных комиссий за объём покупок клиента
                </h2>
                <div className="muted" style={{ fontSize: 13 }}>
                  Автоматически снижает наценку на Stars и Premium, когда сумма доставленных покупок клиента достигает порога. Персональный тариф в карточке клиента всегда имеет приоритет.
                </div>
              </div>
              <div className="row" style={{ gap: 8 }}>
                <button
                  type="button"
                  className="btn sm ghost"
                  disabled={spendTierRows.length >= 20}
                  onClick={() => {
                    const last = spendTierRows[spendTierRows.length - 1];
                    const nextMin = last
                      ? Math.max(1000, (Number(last.minSpentRub) || 0) * 2)
                      : 5000;
                    const defaultStarsPct = liveStarsFeePct;
                    const defaultPremPct = livePremiumFeePct;
                    setSpendTierRows([
                      ...spendTierRows,
                      {
                        minSpentRub: String(nextMin),
                        starsFeePercent: String(
                          Math.max(0, Number((defaultStarsPct * 0.8).toFixed(1))),
                        ),
                        premiumFeePercent: String(
                          Math.max(0, Number((defaultPremPct * 0.8).toFixed(1))),
                        ),
                      },
                    ]);
                  }}
                >
                  <AdminIcon name="plus" size={13} />
                  <span>Добавить уровень</span>
                </button>
                <button
                  type="button"
                  className="btn sm primary"
                  disabled={saveSpendTiers.isPending}
                  onClick={handleSaveSpendTiers}
                >
                  <AdminIcon name="check" size={13} />
                  <span>{saveSpendTiers.isPending ? 'Сохранение…' : 'Сохранить уровни'}</span>
                </button>
              </div>
            </div>

            {spendTierRows.length === 0 ? (
              <div
                style={{
                  padding: '14px 16px',
                  borderRadius: 10,
                  background: 'var(--bg-2)',
                  border: '1px solid var(--hair)',
                  color: 'var(--ink-soft)',
                  fontSize: 13,
                }}
              >
                Ступени оборота пока не заданы — для всех клиентов без индивидуального тарифа действует базовая наценка (+{fmtPct(liveStarsFeePct)}% Stars / +{fmtPct(livePremiumFeePct)}% Premium). Нажмите «Добавить уровень», чтобы настроить прогрессивную шкалу скидок.
              </div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Порог покупок от (₽)</th>
                      <th>Комиссия Stars (%)</th>
                      <th>Комиссия Premium (%)</th>
                      <th>Эквивалент множителя X</th>
                      <th style={{ textAlign: 'right' }}>Действие</th>
                    </tr>
                  </thead>
                  <tbody>
                    {spendTierRows.map((row, idx) => {
                      const sPct = parseNonNegNumber(row.starsFeePercent, 0);
                      const pPct = parseNonNegNumber(row.premiumFeePercent, 0);
                      return (
                        <tr key={idx}>
                          <td>
                            <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
                              <input
                                type="number"
                                min="1"
                                step="500"
                                className="mono"
                                style={{ width: 136 }}
                                placeholder="5000"
                                aria-label={`Ступень ${idx + 1} — порог покупок в рублях`}
                                value={row.minSpentRub}
                                onChange={(e) =>
                                  setSpendTierRows((prev) =>
                                    prev.map((r, i) =>
                                      i === idx ? { ...r, minSpentRub: e.target.value } : r,
                                    ),
                                  )
                                }
                              />
                              <span className="muted">₽</span>
                            </div>
                          </td>
                          <td>
                            <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
                              <input
                                type="number"
                                min="0"
                                max="900"
                                step="0.5"
                                className="mono"
                                style={{ width: 96 }}
                                aria-label={`Ступень ${idx + 1} — комиссия Stars %`}
                                value={row.starsFeePercent}
                                onChange={(e) =>
                                  setSpendTierRows((prev) =>
                                    prev.map((r, i) =>
                                      i === idx ? { ...r, starsFeePercent: e.target.value } : r,
                                    ),
                                  )
                                }
                              />
                              <span className="muted">%</span>
                            </div>
                          </td>
                          <td>
                            <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
                              <input
                                type="number"
                                min="0"
                                max="900"
                                step="0.5"
                                className="mono"
                                style={{ width: 96 }}
                                aria-label={`Ступень ${idx + 1} — комиссия Premium %`}
                                value={row.premiumFeePercent}
                                onChange={(e) =>
                                  setSpendTierRows((prev) =>
                                    prev.map((r, i) =>
                                      i === idx ? { ...r, premiumFeePercent: e.target.value } : r,
                                    ),
                                  )
                                }
                              />
                              <span className="muted">%</span>
                            </div>
                          </td>
                          <td className="mono" style={{ color: 'var(--ink-soft)' }}>
                            Stars ×{fmtMoney(feePercentToMarkup(sPct), 2)} · Premium ×
                            {fmtMoney(feePercentToMarkup(pPct), 2)}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <button
                              type="button"
                              className="btn sm ghost"
                              onClick={() =>
                                setSpendTierRows((prev) => prev.filter((_, i) => i !== idx))
                              }
                            >
                              <AdminIcon name="close" size={12} />
                              <span>Удалить</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {tierMatrix && (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div
                style={{
                  padding: '16px 20px',
                  borderBottom: '1px solid var(--hair)',
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                }}
              >
                <div>
                  <h2 style={{ margin: 0 }}>Итоговые цены для клиентов по ступеням</h2>
                  <div className="muted" style={{ fontSize: 12.5, marginTop: 3 }}>
                    Матрица пересчитывается в реальном времени по мере ввода порогов и процентов выше (даже до нажатия «Сохранить уровни»)
                  </div>
                </div>
                <span className="chip info mono">
                  Ступеней в предпросмотре: {tierMatrix.tiers.length}
                </span>
              </div>

              {tierMatrix.tiers.length === 0 && (
                <div
                  style={{
                    padding: '12px 20px',
                    background: 'rgba(130, 148, 255, 0.08)',
                    borderBottom: '1px solid var(--hair)',
                    color: 'var(--ink-soft)',
                    fontSize: 13,
                  }}
                >
                  Сейчас отображается только базовый тариф. Добавьте ступень кнопкой «Добавить уровень» выше — в таблице сразу появятся колонки с розничными ценами в ₽ и GRAM и расчётом экономии для клиента.
                </div>
              )}

              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Позиция</th>
                      <th>Себестоимость Fragment</th>
                      <th>
                        <div>Базовый тариф</div>
                        <div className="mono" style={{ fontSize: 11, color: 'var(--ink-soft)' }}>
                          (+{fmtPct(tierMatrix.baseStarsFeePercent)}% / +
                          {fmtPct(tierMatrix.basePremiumFeePercent)}%)
                        </div>
                      </th>
                      {tierMatrix.tiers.map((t) => (
                        <th key={`th-${t.index}`}>
                          <div className="mono" style={{ color: '#b4c2ff', fontWeight: 600 }}>
                            От {num(t.minSpentRub)} ₽
                          </div>
                          <div className="mono" style={{ fontSize: 11, color: 'var(--ink-soft)' }}>
                            (+{fmtPct(t.starsFeePercent)}% / +{fmtPct(t.premiumFeePercent)}%)
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tierMatrix.rows.map((row) => {
                      const isUnitStar = row.kind === 'stars' && row.qtyOrMonths === 1;
                      const isPremium = row.kind === 'premium';
                      const rubDigits = isUnitStar ? 3 : isPremium ? 0 : 2;
                      const gramDigits = isPremium ? 2 : 4;
                      return (
                        <tr
                          key={row.key}
                          style={
                            isPremium ? { background: 'rgba(130, 148, 255, 0.04)' } : undefined
                          }
                        >
                          <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <AdminIcon
                                name={isPremium ? 'premium' : 'star'}
                                size={14}
                              />
                              <span>{row.label}</span>
                            </span>
                          </td>
                          <td className="mono" style={{ color: 'var(--ink-soft)', whiteSpace: 'nowrap' }}>
                            <div>{fmtMoney(row.wholesaleRub, isUnitStar ? 3 : 2)} ₽</div>
                            <div className="muted" style={{ fontSize: 11.5 }}>
                              {fmtMoney(row.wholesaleGram, gramDigits)} GRAM
                            </div>
                          </td>
                          <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                            <div style={{ fontWeight: 600 }}>
                              {fmtMoney(row.baseTariffRub, rubDigits)} ₽
                            </div>
                            <div className="muted" style={{ fontSize: 11.5 }}>
                              {fmtMoney(row.baseTariffGram, gramDigits)} GRAM
                            </div>
                          </td>
                          {row.tierCells.map((cell, cIdx) => {
                            const savesMoney = cell.savingsRub > 0;
                            const costsMore = cell.savingsRub < 0;
                            return (
                              <td
                                key={`cell-${row.key}-${cIdx}`}
                                className="mono"
                                style={{ whiteSpace: 'nowrap' }}
                              >
                                <div style={{ fontWeight: 600 }}>
                                  {fmtMoney(cell.retailRub, rubDigits)} ₽
                                </div>
                                <div className="muted" style={{ fontSize: 11.5 }}>
                                  {fmtMoney(cell.retailGram, gramDigits)} GRAM
                                </div>
                                {savesMoney ? (
                                  <div
                                    style={{
                                      fontSize: 11.5,
                                      fontWeight: 600,
                                      color: 'var(--green)',
                                      marginTop: 2,
                                    }}
                                  >
                                    −{fmtMoney(cell.savingsRub, isUnitStar ? 3 : 2)} ₽
                                  </div>
                                ) : costsMore ? (
                                  <div
                                    style={{
                                      fontSize: 11.5,
                                      fontWeight: 600,
                                      color: 'var(--amber)',
                                      marginTop: 2,
                                    }}
                                  >
                                    +{fmtMoney(Math.abs(cell.savingsRub), isUnitStar ? 3 : 2)} ₽ к базе
                                  </div>
                                ) : (
                                  <div
                                    className="muted"
                                    style={{ fontSize: 11, marginTop: 2 }}
                                  >
                                    = базовый тариф
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'packs' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <h2 style={{ marginBottom: 4 }}>Пакеты Stars и лимиты витрины Mini App</h2>
            <div className="muted" style={{ fontSize: 13, marginBottom: 14 }}>
              Быстрые кнопки количества Stars на главном экране покупки. Минимальный заказ Fragment:{' '}
              <span className="mono">{num(STARS_MIN)} Stars</span>, допустимый диапазон произвольного ввода:{' '}
              <span className="mono">
                {num(QTY_LIMITS.min)} – {num(QTY_LIMITS.max)} Stars
              </span>
              .
            </div>

            {packFields.length === 0 ? (
              <div className="muted">Настраиваемые параметры витрины не найдены.</div>
            ) : (
              packFields.map((f, idx) => {
                const def = Array.isArray(f.default) ? f.default.join(', ') : String(f.default);
                const cur = Array.isArray(f.value) ? f.value.join(', ') : String(f.value);
                const changed = (draft[f.key] ?? '').trim() !== cur.trim();
                return (
                  <div
                    key={f.key}
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 14,
                      padding: '12px 0',
                      borderTop: idx > 0 ? '1px solid var(--hair)' : 'none',
                    }}
                  >
                    <div style={{ flex: '1 1 240px', minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }}>{f.label}</div>
                      <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
                        {f.hint ? `${f.hint} · ` : ''}ключ: <span className="mono">{f.key}</span> · по умолчанию:{' '}
                        <span className="mono">{def}</span>
                      </div>
                    </div>
                    <div className="row" style={{ gap: 10, flex: '1 1 240px', justifyContent: 'flex-end' }}>
                      <input
                        type="text"
                        className="mono"
                        style={{ flex: '1 1 160px', minWidth: 0 }}
                        aria-label={f.label}
                        value={draft[f.key] ?? ''}
                        onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                      />
                      <button
                        type="button"
                        className="btn sm primary"
                        disabled={!changed || saveSetting.isPending}
                        onClick={() => submitField(f)}
                      >
                        <AdminIcon name="check" size={13} />
                        <span>Сохранить</span>
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {parsedStarPacksPreview.length > 0 && (
            <div className="card">
              <h2 style={{ marginBottom: 4 }}>Предпросмотр кнопок пакетов в Mini App</h2>
              <div className="muted" style={{ fontSize: 12.5, marginBottom: 14 }}>
                Расчёт показан по текущему базовому множителю Stars ×{fmtMoney(liveStarsMult, 2)} (+
                {fmtPct(liveStarsFeePct)}%)
              </div>
              <div className="grid cols-4">
                {parsedStarPacksPreview.map((packQty, idx) => {
                  const unit = previewData
                    ? computeRetailStarPricing({
                        baseStarGram: previewData.fragment.baseStarGram,
                        baseStarUsd: previewData.fragment.baseStarUsd,
                        gramRub: previewData.fx.gramRub,
                        usdRub: previewData.fx.usdRub,
                        markup: liveStarsMult,
                        paymentMethod: previewData.paymentMethod,
                      })
                    : null;
                  const packRub = unit ? Number((unit.retailRub * packQty).toFixed(2)) : null;
                  const packGram = unit
                    ? milliToGram(computeOrderMilliGram(packQty, unit.retailGram))
                    : null;
                  return (
                    <div key={`${packQty}-${idx}`} className="kpi-card purple">
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          fontWeight: 600,
                          fontSize: 16,
                        }}
                      >
                        <AdminIcon name="star" size={16} />
                        <span className="mono">{num(packQty)} Stars</span>
                      </div>
                      {packRub !== null && packGram !== null && (
                        <div className="kpi-sub mono" style={{ marginTop: 8 }}>
                          {fmtMoney(packRub)} ₽ · {fmtMoney(packGram, 3)} GRAM
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
  // END_BLOCK_PRICING_PAGE
}
